import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';
import { Plus, PencilSimple, Trash, Eye, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  slug: '',
  title_tr: '',
  title_en: '',
  excerpt_tr: '',
  excerpt_en: '',
  content_tr: '',
  content_en: '',
  meta_description_tr: '',
  meta_description_en: '',
  cover_image: '',
  tags: '',
  author: 'GrowthRIQ Team',
  published: true,
};

const BlogAdminPanel = () => {
  const { t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null); // null => create mode
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/blog/admin/all`, {
        headers: getAuthHeaders(),
      });
      setPosts(res.data);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openCreate = () => {
    setEditingSlug(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = async (slug) => {
    try {
      const res = await axios.get(`${API_URL}/blog/admin/${slug}`, {
        headers: getAuthHeaders(),
      });
      const p = res.data;
      setForm({
        slug: p.slug,
        title_tr: p.title_tr || '',
        title_en: p.title_en || '',
        excerpt_tr: p.excerpt_tr || '',
        excerpt_en: p.excerpt_en || '',
        content_tr: p.content_tr || '',
        content_en: p.content_en || '',
        meta_description_tr: p.meta_description_tr || '',
        meta_description_en: p.meta_description_en || '',
        cover_image: p.cover_image || '',
        tags: (p.tags || []).join(', '),
        author: p.author || 'GrowthRIQ Team',
        published: p.published,
      });
      setEditingSlug(slug);
      setDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load post');
    }
  };

  const handleDelete = async () => {
    const slug = deleteTarget;
    if (!slug) return;
    try {
      await axios.delete(`${API_URL}/blog/admin/${slug}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t('admin.blog.deleted'));
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSave = async () => {
    if (!form.slug || !form.title_tr || !form.title_en || !form.content_tr || !form.content_en) {
      toast.error(t('admin.blog.form.required'));
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags
        ? form.tags.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try {
      if (editingSlug) {
        await axios.put(`${API_URL}/blog/admin/${editingSlug}`, payload, {
          headers: getAuthHeaders(),
        });
      } else {
        await axios.post(`${API_URL}/blog/admin`, payload, {
          headers: getAuthHeaders(),
        });
      }
      toast.success(t('admin.blog.saved'));
      setDialogOpen(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleAiGenerate = async () => {
    if (!aiTopic.trim() || aiTopic.trim().length < 3) {
      toast.error(t('admin.blog.ai.topicRequired'));
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/blog/admin/ai-draft`,
        {
          topic: aiTopic.trim(),
          keywords: aiKeywords
            ? aiKeywords.split(',').map((s) => s.trim()).filter(Boolean)
            : null,
        },
        { headers: getAuthHeaders(), timeout: 120000 }
      );
      const d = res.data;
      setForm({
        slug: d.slug || '',
        title_tr: d.title_tr || '',
        title_en: d.title_en || '',
        excerpt_tr: d.excerpt_tr || '',
        excerpt_en: d.excerpt_en || '',
        content_tr: d.content_tr || '',
        content_en: d.content_en || '',
        meta_description_tr: d.meta_description_tr || '',
        meta_description_en: d.meta_description_en || '',
        cover_image: '',
        tags: (d.tags || []).join(', '),
        author: 'GrowthRIQ Team',
        published: false,
      });
      setEditingSlug(null);
      setAiOpen(false);
      setAiTopic('');
      setAiKeywords('');
      setDialogOpen(true);
      toast.success(t('admin.blog.ai.generated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.blog.ai.failed'));
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="blog-admin-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
        <h3 className="text-lg font-semibold text-[#09090B]">
          {t('admin.blog')} ({posts.length})
        </h3>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAiOpen(true)}
            variant="outline"
            className="rounded-none border-[#0F1B36] text-[#0F1B36] hover:bg-[#0F1B36]/5"
            data-testid="blog-admin-ai-button"
          >
            <Sparkle size={16} className="mr-2" weight="fill" />
            {t('admin.blog.ai.button')}
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
            data-testid="blog-admin-new-button"
          >
            <Plus size={16} className="mr-2" />
            {t('admin.blog.new')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[#52525B]">{t('common.loading')}</div>
      ) : posts.length === 0 ? (
        <div className="p-8 text-center text-[#52525B]" data-testid="blog-admin-empty">
          {t('admin.blog.empty')}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.blog.table.slug')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.blog.table.title')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.blog.table.status')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.blog.table.views')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.blog.table.date')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B] text-right">{t('admin.blog.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((p) => (
              <TableRow key={p.id} data-testid={`blog-row-${p.slug}`}>
                <TableCell className="font-mono text-xs text-[#52525B]">{p.slug}</TableCell>
                <TableCell className="font-medium max-w-xs truncate">{p.title_tr}</TableCell>
                <TableCell>
                  {p.published ? (
                    <span className="text-xs bg-[#16A34A]/10 text-[#16A34A] px-2 py-1">
                      {t('admin.blog.status.published')}
                    </span>
                  ) : (
                    <span className="text-xs bg-[#F4F4F5] text-[#52525B] px-2 py-1">
                      {t('admin.blog.status.draft')}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{p.views}</TableCell>
                <TableCell className="text-sm text-[#52525B]">
                  {new Date(p.published_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-8 h-8 text-[#52525B] hover:text-[#0F1B36] hover:bg-[#F4F4F5]"
                      title={t('admin.blog.preview')}
                      data-testid={`blog-preview-${p.slug}`}
                    >
                      <Eye size={16} />
                    </a>
                    <button
                      onClick={() => openEdit(p.slug)}
                      className="inline-flex items-center justify-center w-8 h-8 text-[#52525B] hover:text-[#0F1B36] hover:bg-[#F4F4F5]"
                      title={t('admin.blog.edit')}
                      data-testid={`blog-edit-${p.slug}`}
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(p.slug)}
                      className="inline-flex items-center justify-center w-8 h-8 text-[#52525B] hover:text-[#E60000] hover:bg-[#F4F4F5]"
                      title={t('admin.blog.delete')}
                      data-testid={`blog-delete-${p.slug}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="blog-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingSlug ? t('admin.blog.edit') : t('admin.blog.new')} — {t('admin.blog.form.title')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.blog.form.dialogHelp') || 'Markdown editörü ile blog yazınızı oluşturun veya düzenleyin. İki dilde içerik girebilirsiniz.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="slug">{t('admin.blog.form.slug')} *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''))}
                disabled={!!editingSlug}
                className="rounded-none"
                data-testid="blog-form-slug"
              />
              <p className="text-xs text-[#52525B] mt-1">{t('admin.blog.form.slugHelp')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title_tr">{t('admin.blog.form.titleTr')} *</Label>
                <Input
                  id="title_tr"
                  value={form.title_tr}
                  onChange={(e) => updateField('title_tr', e.target.value)}
                  className="rounded-none"
                  data-testid="blog-form-title-tr"
                />
              </div>
              <div>
                <Label htmlFor="title_en">{t('admin.blog.form.titleEn')} *</Label>
                <Input
                  id="title_en"
                  value={form.title_en}
                  onChange={(e) => updateField('title_en', e.target.value)}
                  className="rounded-none"
                  data-testid="blog-form-title-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="excerpt_tr">{t('admin.blog.form.excerptTr')}</Label>
                <Textarea
                  id="excerpt_tr"
                  value={form.excerpt_tr}
                  onChange={(e) => updateField('excerpt_tr', e.target.value)}
                  className="rounded-none min-h-[70px]"
                  data-testid="blog-form-excerpt-tr"
                />
              </div>
              <div>
                <Label htmlFor="excerpt_en">{t('admin.blog.form.excerptEn')}</Label>
                <Textarea
                  id="excerpt_en"
                  value={form.excerpt_en}
                  onChange={(e) => updateField('excerpt_en', e.target.value)}
                  className="rounded-none min-h-[70px]"
                  data-testid="blog-form-excerpt-en"
                />
              </div>
            </div>

            <Tabs defaultValue="tr" className="w-full">
              <TabsList className="bg-[#F4F4F5] p-1 rounded-none">
                <TabsTrigger value="tr" className="rounded-none">TR İçerik</TabsTrigger>
                <TabsTrigger value="en" className="rounded-none">EN Content</TabsTrigger>
              </TabsList>
              <TabsContent value="tr">
                <Label>{t('admin.blog.form.contentTr')} *</Label>
                <div data-color-mode="light" className="mt-1" data-testid="blog-form-content-tr">
                  <MDEditor
                    value={form.content_tr}
                    onChange={(v) => updateField('content_tr', v || '')}
                    height={420}
                    preview="live"
                    visibleDragbar={false}
                  />
                </div>
              </TabsContent>
              <TabsContent value="en">
                <Label>{t('admin.blog.form.contentEn')} *</Label>
                <div data-color-mode="light" className="mt-1" data-testid="blog-form-content-en">
                  <MDEditor
                    value={form.content_en}
                    onChange={(v) => updateField('content_en', v || '')}
                    height={420}
                    preview="live"
                    visibleDragbar={false}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="meta_tr">{t('admin.blog.form.metaTr')}</Label>
                <Textarea
                  id="meta_tr"
                  value={form.meta_description_tr}
                  onChange={(e) => updateField('meta_description_tr', e.target.value)}
                  className="rounded-none min-h-[60px]"
                  maxLength={200}
                  data-testid="blog-form-meta-tr"
                />
              </div>
              <div>
                <Label htmlFor="meta_en">{t('admin.blog.form.metaEn')}</Label>
                <Textarea
                  id="meta_en"
                  value={form.meta_description_en}
                  onChange={(e) => updateField('meta_description_en', e.target.value)}
                  className="rounded-none min-h-[60px]"
                  maxLength={200}
                  data-testid="blog-form-meta-en"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cover_image">{t('admin.blog.form.coverImage')}</Label>
                <Input
                  id="cover_image"
                  value={form.cover_image}
                  onChange={(e) => updateField('cover_image', e.target.value)}
                  placeholder="https://..."
                  className="rounded-none"
                  data-testid="blog-form-cover"
                />
              </div>
              <div>
                <Label htmlFor="tags">{t('admin.blog.form.tags')}</Label>
                <Input
                  id="tags"
                  value={form.tags}
                  onChange={(e) => updateField('tags', e.target.value)}
                  placeholder="e-ihracat, swot, rehber"
                  className="rounded-none"
                  data-testid="blog-form-tags"
                />
              </div>
              <div>
                <Label htmlFor="author">{t('admin.blog.form.author')}</Label>
                <Input
                  id="author"
                  value={form.author}
                  onChange={(e) => updateField('author', e.target.value)}
                  className="rounded-none"
                  data-testid="blog-form-author"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Checkbox
                id="published"
                checked={form.published}
                onCheckedChange={(v) => updateField('published', !!v)}
                data-testid="blog-form-published"
              />
              <Label htmlFor="published" className="cursor-pointer">
                {t('admin.blog.form.published')}
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-none"
              data-testid="blog-form-cancel"
            >
              {t('admin.blog.form.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
              data-testid="blog-form-save"
            >
              {saving ? t('common.loading') : t('admin.blog.form.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="blog-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="fill" className="text-[#0F1B36]" />
              {t('admin.blog.ai.title')}
            </DialogTitle>
            <DialogDescription>{t('admin.blog.ai.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="ai-topic">{t('admin.blog.ai.topicLabel')} *</Label>
              <Input
                id="ai-topic"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder={t('admin.blog.ai.topicPlaceholder')}
                className="rounded-none"
                data-testid="blog-ai-topic"
                disabled={aiGenerating}
              />
            </div>
            <div>
              <Label htmlFor="ai-keywords">{t('admin.blog.ai.keywordsLabel')}</Label>
              <Input
                id="ai-keywords"
                value={aiKeywords}
                onChange={(e) => setAiKeywords(e.target.value)}
                placeholder="e-ihracat, amazon, fba"
                className="rounded-none"
                data-testid="blog-ai-keywords"
                disabled={aiGenerating}
              />
            </div>
            <p className="text-xs text-[#52525B] italic">
              {t('admin.blog.ai.help')}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAiOpen(false)}
              disabled={aiGenerating}
              className="rounded-none"
              data-testid="blog-ai-cancel"
            >
              {t('admin.blog.form.cancel')}
            </Button>
            <Button
              onClick={handleAiGenerate}
              disabled={aiGenerating}
              className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
              data-testid="blog-ai-generate"
            >
              <Sparkle size={14} className="mr-2" weight="fill" />
              {aiGenerating ? t('admin.blog.ai.generating') : t('admin.blog.ai.generate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="blog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.blog.delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.blog.confirmDelete')}
              {deleteTarget ? ` (${deleteTarget})` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="blog-delete-cancel">
              {t('admin.blog.form.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E60000] hover:bg-[#E60000]/90 text-white rounded-none"
              data-testid="blog-delete-confirm-button"
            >
              {t('admin.blog.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlogAdminPanel;
